import at from '../constants/ActionTypes/MeshBot';
import { MESHBOT_TYPES } from '../containers/Ezlo/EzloMeshbots/constants';
import { bugsnagNotify } from '../containers/ErrorBoundary/utils';
import actions from '../actions/MesheneActions';
import { SYNC_OPERATION } from './buildListSyncCondition';

/**
 * Builds a fallback thunk for when polling times out and the expected list change
 * was never observed. Manually applies the operation's effect to the Redux store.
 *
 * - DELETE: removes the item from the cloud MeshBot list in Redux.
 * - CREATE: optimistically adds the item to the top of the list.
 * - UPDATE: replaces the item in the list with a merged snapshot.
 *
 * Intended to be passed as `onSyncTimeout` to {@link pollUntilMeshBotSynced}.
 *
 * @param {string} operation - One of {@link SYNC_OPERATION} values.
 * @param {string} uuid - UUID of the affected MeshBot.
 * @param {Object} params - The original API request payload sent for this operation.
 * @param {Object|null} previousMeshBot - Snapshot of the MeshBot state before the operation
 *   (used for UPDATE to merge with new params).
 * @returns {Function} Redux thunk `(dispatch, getState) => Promise<void>`.
 *
 * @example
 * dispatch(buildSyncTimeoutFallback(SYNC_OPERATION.DELETE, uuid, params, null));
 * dispatch(buildSyncTimeoutFallback(SYNC_OPERATION.CREATE, uuid, params, null));
 * dispatch(buildSyncTimeoutFallback(SYNC_OPERATION.UPDATE, uuid, params, previousMeshBot));
 */
export const buildSyncTimeoutFallback = (operation, uuid, params, previousMeshBot) => async (dispatch, getState) => {
    switch (operation) {
        case SYNC_OPERATION.DELETE: {
            dispatch(actions.clearCloudMeshBotListFromDeletedMeshBot(uuid));
            break;
        }
        case SYNC_OPERATION.CREATE: {
            try {
                dispatch({
                    type: at.ADD_OPTIMISTIC_CLOUD_MESHBOT,
                    meshBot: { ...params, uuid },
                });
            } catch (err) {
                bugsnagNotify(err, { uuid, callFn: 'buildSyncTimeoutFallback/CREATE' });
            }
            break;
        }
        case SYNC_OPERATION.UPDATE: {
            try {
                const {
                    meshBot: {
                        [MESHBOT_TYPES.CLOUD]: { cloudMeshBot },
                    },
                } = getState();
                const updatedList = cloudMeshBot.map((bot) =>
                    bot.uuid === uuid
                        ? {
                              ...previousMeshBot,
                              ...params,
                          }
                        : bot,
                );

                dispatch({ type: at.SET_CLOUD_MESHBOT_LIST, updatedCloudMeshBotsList: updatedList });
            } catch (err) {
                bugsnagNotify(err, { uuid, callFn: 'buildSyncTimeoutFallback/UPDATE' });
            }
            break;
        }
    }
};
